import { LectioJSUtils, STagged } from './../LectioJSUtils';
import { HtmlUtilities } from "./../HtmlUtilities";
import { LectioFeatureDetection } from '../LectioFeatureDetection';

interface LCDocumentChapterChooserState3 {
	Ids: TocItemId[];
}

type TocItemId = STagged<'TocItemId'>;

export namespace LCDocumentChapterChooser3 {
	function IE11IsDoubleInitialization(element: HTMLElement): boolean {
		// Beskyttelse mod dobbelt initialisering i materialevaelger i IE 11.
		// Sker ikke konsekvent...
		const attrib = 'lec-nodoubleinit';
		if (element.getAttribute(attrib))
			return true;
		element.setAttribute(attrib, 'si');
		return false;
	}

	enum CheckboxIcon {
		checked = '/lectio/img/checkbox_checked.auto',
		unchecked = '/lectio/img/checkbox_unchecked.auto',
		disabled = '/lectio/img/checkbox_disabled_checked.auto',
	}

	export function InitializeSelection(
		tocListId: string,
		contentContainerId: string,
		contentType: "LCDoc" | "Homework" | null
	): void {
		const containerOrNull = document.getElementById(tocListId);
		if (!containerOrNull)
			throw new Error(`TOC container '${tocListId}' findes ikke.`);
		const tocContainer = containerOrNull;

		const contentContainer = contentContainerId ? document.getElementById(contentContainerId) : null;
		if (contentContainerId && !contentContainer)
			throw new Error(`Content container '${contentContainerId}' findes ikke.`);

		if (contentContainer && IE11IsDoubleInitialization(contentContainer))
			return;

		const state = GetState(tocContainer);

		// Klik ikke-valgt o:
		// u := FindUnderafsnit(o)
		// newState1 := AfmarkerValgte(state, u)
		// newState2 := Marker(newState1, o)

		// Klik valgt o:
		// newState := Afmarker(state, o)

		// TOC-ens datastruktur fremgaar kun af dens html-struktur, og derfor
		// forventes den at have en bestemt struktur.
		// PT (20170731) er det enten en flad liste LI-elementer med attributter
		// data-level og data-value (for forloebsmat), eller en indlejret UL
		// pr.niveau (for aktiviteter med lektier).
		// Checkbokse for indholdsvisningssiden forventes derimod blot at have
		// data-value-attribut, saa de kan findes ud fra deres ID.

		$(tocContainer).on('click', 'img[data-role=cb]',
			evt => OnCheckboxClick(evt, state, tocContainer, contentContainer, true));
		if (contentContainer) {
			$(contentContainer).on('click', 'img[data-role=cb]',
				evt => OnCheckboxClick(evt, state, tocContainer, contentContainer, false));
			switch (contentType) {
				case 'LCDoc':
					CreateCheckboxes_LCDoc(contentContainer);
					break;
				case 'Homework':
					CreateCheckboxes_Homework(contentContainer);
					break;
				case null:
					break;
				default:
					throw new Error('Ugyldig vaerdi: ' + contentType);
			}
		}

		for (const id of state.Ids) {
			const $item = tocContainer.querySelectorAll(`li[data-value="${id}"]`);
			if ($item.length !== 1)
				throw new Error('Fandt ikke eet element for id ' + id + ', fandt ' + $item.length);
			const item = LectioJSUtils.GetAssertedType($item[0], HTMLLIElement, 'li-element');
			const subsections = GetSubsectionsFromDomDeep(item);

			UpdateCheckboxDisplay(item, contentContainer, subsections, true);
		}
	}

	export function InitializeEditorToc(tocListId: string) {

		const containerOrNull = document.getElementById(tocListId);
		if (!containerOrNull)
			throw new Error(`TOC container '${tocListId}' findes ikke.`);
		const tocContainer = containerOrNull;

		// Haandterer her manuelt klik paa links i toc, for at kunne scrolle
		// lidt ekstra ned naar indholdet bliver vist i editoren.
		// Det lader til at vaere noedvendigt at goere det saaleedes for at
		// scrolle lidt mindre ned for at scroll target ikke gemmer sig bag
		// editorens fixed toolbar.
		// Men vi vil egentlig helst have browseren til at haandtere det selv
		// naar det er muligt, saa vi blander os kun i det naevnte
		// editor-tilfaelde.
		$(tocContainer).on('click', 'a', evt => {
			const clickTarget = LectioJSUtils.GetAssertedType(evt.target, HTMLAnchorElement, 'element type "a".');
			const href = clickTarget.getAttribute('href');
			if (!href)
				return;
			if (!/^#/.test(href))
				return;
			const id = href.substr(1, href.length - 1);
			const linkTarget = document.getElementById(id);
			if (!linkTarget)
				return;
			const targetIsInEditor = $(linkTarget).parents('.cke_contents').length !== 0;
			if (!targetIsInEditor)
				return;

			evt.preventDefault();

			// scroll som browseren ville have gjort det, eller i det mindste
			// saa taet paa som muligt.
			// Ellers spaender vi pludselig ben for smooth scrooling eller anden
			// opfoersel som brugeren forventer.
			// https://html.spec.whatwg.org/multipage/browsing-the-web.html#scroll-to-fragid
			if (LectioFeatureDetection.SupportsScrollOptions())
				linkTarget.scrollIntoView({ behavior: 'auto', block: 'start', inline: 'nearest' });
			else
				linkTarget.scrollIntoView();
			window.scrollBy(0, (70 + 28) * -1);
		});
	}

	function OnCheckboxClick(
		evt: JQuery.Event,
		state: LCDocumentChapterChooserState3,
		tocContainer: HTMLElement,
		contentContainer: HTMLElement | null,
		isTocClick: boolean
	) {
		let item: HTMLElement;
		if (isTocClick) {
			item = LectioJSUtils.GetAssertedType(
				$((evt as any).target).parents('li').first()[0],
				HTMLLIElement, 'li-element');
		}
		else
			item = (evt as any).target as HTMLElement;
		const idString = item.getAttribute('data-value');
		if (!idString) {
			LectioJSUtils.LogWarn('LCDocumentChapterChooser3: data-value not found.');
			return;
		}
		const id = TryParseTocItemId(idString);
		if (id === 'narj')
			throw new Error('data-value har uventet format.');
		HandleIdClick(id, state, tocContainer, contentContainer);
	}

	function ParseTocItemId(s: string): TocItemId {
		const rv = TryParseTocItemId(s);
		if (rv === 'narj')
			throw new Error('Lader ikke til at vaere TocItemId: ' + s);
		return rv;
	}

	function TryParseTocItemId(s: string): TocItemId | 'narj' {
		const looksRightRegex = /^[a-z]{0,3}[0-9]{6,12}$/i;
		if (!looksRightRegex.test(s))
			return 'narj';
		return s as TocItemId;
	}

	function HandleIdClick(
		id: TocItemId,
		state: LCDocumentChapterChooserState3,
		tocContainer: HTMLElement,
		contentContainer: HTMLElement | null
	) {
		const idIndex = state.Ids.indexOf(id);
		const wasSelected = idIndex !== -1;
		const tocItem = LectioJSUtils.GetAssertedType(
			tocContainer.querySelector(`[data-value="${id}"]`),
			HTMLLIElement, 'li-element');

		const ancestorIds = GetAncestorIds(tocContainer, tocItem);
		const someAncestorIsAlreadySelected = ancestorIds
			.filter(val => state.Ids.indexOf(val) !== -1).length > 0;
		if (someAncestorIsAlreadySelected)
			return;

		const subsections = GetSubsectionsFromDomDeep(tocItem);
		console.debug('handleidclick, subsections: ', subsections);

		if (wasSelected) {
			state.Ids.splice(idIndex, 1);
			for (const subsection of subsections) {
				const idx = state.Ids.indexOf(subsection.value);
				if (idx !== -1)
					throw new Error('underafsnit var markeret?');
			}
		} else {
			for (const subsection of subsections) {
				const idx = state.Ids.indexOf(subsection.value);
				if (idx !== -1)
					state.Ids.splice(idx, 1);
			}
			// 20171212 RH: Tilfoej *efter* at have fjernet valgte underafsnit,
			// for praesentationer paa aktiviteter har samme ID som
			// aktiviteten: Hvis vi tilfoejer det foer, bliver det fjernet igen,
			// naar ovenstaaende linier naar til praesentationen.
			state.Ids.push(id);
		}
		UpdateCheckboxDisplay(tocItem, contentContainer, subsections, !wasSelected);

		SaveState(tocContainer, state);
	}

	function UpdateCheckboxDisplay(
		tocItem: HTMLLIElement,
		contentContainer: HTMLElement | null,
		subsections: { item: HTMLLIElement, value: TocItemId }[],
		mark: boolean
	): void {
		const [icon1, icon2] = !mark
			? [CheckboxIcon.unchecked, CheckboxIcon.unchecked]
			: [CheckboxIcon.checked, CheckboxIcon.disabled];

		$(tocItem).find('img[data-role=cb]').attr('src', icon1);
		if (contentContainer)
			$(contentContainer)
				.find('img[data-role=cb][data-value="' + GetDataValue(tocItem) + '"]')
				.attr('src', icon1);

		for (const subsection of subsections) {
			$(subsection.item).find('img[data-role=cb]').attr('src', icon2);
			if (contentContainer)
				$(contentContainer)
					.find('img[data-role=cb][data-value="' + subsection.value + '"]')
					.attr('src', icon2);
		}
	}

	function CreateCheckbox(id: TocItemId): HTMLElement {
		const cb = $('<img src="/lectio/img/checkbox_unchecked.auto" data-role="cb" class="header-checkbox" />');
		cb.attr('data-value', id);

		return cb[0];
	}

	function CreateCheckboxes_LCDoc(container: HTMLElement): void {
		const parent = container.querySelector('[data-local-id=content]');
		if (!parent)
			throw new Error('Har ikke parent-element.');
		let rootElement = parent.firstElementChild;
		while (rootElement) {
			const isHeader = 'H1 H2 H3 H4 H5 H6'.indexOf(rootElement.nodeName) !== -1;
			if (!isHeader) {
				rootElement = rootElement.nextElementSibling;
				continue;
			}

			const id = ParseTocItemId(rootElement.getAttribute('id') || '🐱');

			$(rootElement).prepend(CreateCheckbox(id));

			rootElement = rootElement.nextElementSibling;
		}
	}

	function CreateCheckboxes_Homework(container: HTMLElement) {
		// checkbokse for lektier i indholdsvisningen.
		const homeworkArticles = $(container).find('a + article');
		for (const art of homeworkArticles.toArray()) {
			const a = $(art).prev();
			if (!(a[0] instanceof HTMLAnchorElement))
				throw new Error('huh, ikke A?');

			const idRaw = a.attr('id');
			if (!idRaw)
				throw new Error('Huh, A har ikke id?');

			const tocItemId = ParseTocItemId(idRaw);
			const firstHeader = art.querySelector(':first-child');
			if (!(firstHeader && 'H1 H2 H3 H4 H5 H6'.indexOf(firstHeader.nodeName) !== -1))
				continue;

			const cb = CreateCheckbox(tocItemId);
			const imgspan = $(firstHeader).find('> span').has('img');
			if (imgspan.length)
				$(imgspan).prepend(cb);
			else
				$(firstHeader).prepend(cb);
		}

		const prexxx = $(container).find('h2[data-role=presentation-activity-header]');
		for (const headerParent of prexxx.toArray()) {
			const tocItemId = ParseTocItemId(headerParent.getAttribute('id') || '🐱');

			const cb = CreateCheckbox(tocItemId);
			$(headerParent).prepend(cb);
		}
	}

	export function GetAncestorIds(tocContainer: HTMLElement, tocItem: HTMLLIElement): TocItemId[] {
		// Naar der klikkes paa en checkboks der repraesenterer et afsnit, vil
		// vi gerne vide om afsnittet allerede implicit er valgt idet et
		// indeholdende afsnit er valgt. I givet fald vil vi ikke goere noget.
		// For at goere det, skal vi udlede hvilke afsnit afsnittet er del af.
		// I stedet for at gennemloebe dommen opad, bygger vi paa
		// GetSubsectionsFromDomDeep, for at undgaa at kode antagelser om
		// strukturen flere steder end noedvendigt.

		const id = GetDataValue(tocItem);
		if (id === 'resource-root')
			throw new Error('GetAncestorIds: Hvorfor resource-root?');

		const containingSectionIds: TocItemId[] = [];
		for (const someTocItem of tocContainer.querySelectorAll<HTMLLIElement>('li[data-value]')) {
			if (someTocItem === tocItem)
				break;

			const subsections = GetSubsectionsFromDomDeep(someTocItem);
			const tocid = GetDataValue(someTocItem);
			if (tocid === 'resource-root')
				continue;
			if (subsections.filter(ss => ss.value === id).length === 1)
				containingSectionIds.push(tocid);
		}

		return containingSectionIds;
	}

	function GetDataValue(element: HTMLElement): TocItemId | 'resource-root' {
		const str = element.getAttribute('data-value');
		if (!str)
			throw new Error('Mangler data-value.');
		if (str === 'resource-root')
			return str;
		const xx = TryParseTocItemId(str);
		if (xx === 'narj')
			throw new Error('data-value har ikke-numerisk format.');
		return xx;
	}

	function GetSubsectionsFromDomDeep(item: HTMLLIElement): { item: HTMLLIElement, value: TocItemId }[] {
		LectioJSUtils.AssertArgument(item instanceof HTMLLIElement, "item er LI");

		const thisLevel = parseInt(item.getAttribute('data-level') || "x", 10);
		const retval: { item: HTMLLIElement, value: TocItemId }[] = [];

		const acceptNode = (n: Node) => n instanceof HTMLLIElement ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
		const walker = LectioFeatureDetection.IsIE()
			? (document as any).createTreeWalker(item.parentElement!, NodeFilter.SHOW_ELEMENT,
				acceptNode, false)
			: (document as any).createTreeWalker(item.parentElement!, NodeFilter.SHOW_ELEMENT,
				{ acceptNode: acceptNode }, false);
		const skipper1 = LectioJSUtils.NewSkipUntil();
		const skipper2 = LectioJSUtils.NewSkip(1);

		while (walker.nextNode()) {
			const next = LectioJSUtils.GetAssertedType(walker.currentNode, HTMLLIElement, 'li');
			if (!(skipper1(next === item) && skipper2()))
				continue;

			const level = parseInt(next.getAttribute('data-level') || "x", 10);
			if (!(level > thisLevel))
				break;
			const value = GetDataValue(next);
			if (value !== 'resource-root')
				retval.push({ item: next, value: value });
		}

		return retval;
	}

	function GetHiddenStateField(container: HTMLElement): HTMLInputElement {
		const stateinput = container.querySelectorAll('input[type=hidden]');
		if (stateinput.length !== 1)
			throw new Error('Der er ikke een hidden i containeren, men ' + stateinput.length + '.');
		return LectioJSUtils.GetAssertedType(stateinput[0], HTMLInputElement, 'input');
	}

	function GetState(container: HTMLElement): LCDocumentChapterChooserState3 {
		const stateinput = GetHiddenStateField(container);
		const state = JSON.parse(stateinput.value) as LCDocumentChapterChooserState3;
		if (state.Ids == null) {
			state.Ids = [];
		}
		return state;
	}

	function SaveState(container: HTMLElement, state: LCDocumentChapterChooserState3): void {
		const stateinput = GetHiddenStateField(container);
		const json = JSON.stringify(state);
		stateinput.value = json;
	}
}
